import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { ProductCard, ProductGrid, Skeleton, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@kamalkari/ui';
import { useProductDetailStore } from '@/stores/productDetailStore';
import type { Product, Category } from '@kamalkari/types';

export const Route = createFileRoute('/products')({
  component: ProductsPage,
});

const API_URL = import.meta.env.VITE_API_URL || 'https://0vkok222yh.execute-api.us-east-1.amazonaws.com';

function ProductsPage() {
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [sortBy, setSortBy] = useState<string>('featured');
  const openProduct = useProductDetailStore((s) => s.openProduct);

  const { data: categories = [] } = useQuery({
    queryKey: ['categories'],
    queryFn: async () => {
      const response = await fetch(`${API_URL}/v1/categories`);
      if (!response.ok) throw new Error('Failed to fetch categories');
      const data = await response.json();
      return data.data?.items || data.items || [];
    },
  });

  const { data: products = [], isLoading } = useQuery({
    queryKey: ['products', selectedCategory],
    queryFn: async () => {
      const url = selectedCategory === 'all'
        ? `${API_URL}/v1/products`
        : `${API_URL}/v1/products?categoryId=${selectedCategory}`;
      const response = await fetch(url);
      if (!response.ok) throw new Error('Failed to fetch products');
      const data = await response.json();
      return data.data?.items || data.items || [];
    },
  });

  const sortedProducts = [...products].sort((a: Product, b: Product) => {
    switch (sortBy) {
      case 'price-low':
        return a.price - b.price;
      case 'price-high':
        return b.price - a.price;
      case 'newest':
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      default:
        return a.sortOrder - b.sortOrder;
    }
  });

  return (
    <main className="container-page py-12">
      <div className="mb-12">
        <h1 className="text-4xl font-serif text-warm-900 mb-4">Our Collection</h1>
        <p className="text-warm-600 max-w-2xl">
          Each piece in our collection is handcrafted by skilled artisans using traditional
          Kalamkari techniques passed down through generations.
        </p>
      </div>

      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8 pb-8 border-b border-warm-200">
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setSelectedCategory('all')}
            className={`px-4 py-2 text-sm rounded-sm transition-colors ${
              selectedCategory === 'all'
                ? 'bg-warm-800 text-warm-50'
                : 'bg-warm-100 text-warm-700 hover:bg-warm-200'
            }`}
          >
            All
          </button>
          {categories.map((category: Category) => (
            <button
              key={category.categoryId}
              onClick={() => setSelectedCategory(category.categoryId)}
              className={`px-4 py-2 text-sm rounded-sm transition-colors ${
                selectedCategory === category.categoryId
                  ? 'bg-warm-800 text-warm-50'
                  : 'bg-warm-100 text-warm-700 hover:bg-warm-200'
              }`}
            >
              {category.name}
            </button>
          ))}
        </div>

        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Sort by" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="featured">Featured</SelectItem>
            <SelectItem value="newest">Newest</SelectItem>
            <SelectItem value="price-low">Price: Low to High</SelectItem>
            <SelectItem value="price-high">Price: High to Low</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <ProductGrid columns={4}>
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="space-y-4">
              <Skeleton className="aspect-[3/4] rounded-sm" />
              <Skeleton className="h-4 w-2/3" />
              <Skeleton className="h-4 w-1/3" />
            </div>
          ))}
        </ProductGrid>
      ) : (
        <ProductGrid columns={4}>
          {sortedProducts.map((product: Product) => (
            <ProductCard
              key={product.productId}
              id={product.productId}
              name={product.name}
              price={product.price}
              compareAtPrice={product.compareAtPrice}
              image={product.images[0]}
              category={categories.find((c: Category) => c.categoryId === product.categoryId)?.name}
              onClick={openProduct}
            />
          ))}
        </ProductGrid>
      )}

      {sortedProducts.length === 0 && !isLoading && (
        <div className="text-center py-20">
          <p className="text-warm-600">No products found in this category.</p>
        </div>
      )}
    </main>
  );
}
